import { FormField, FormItem, FormControl, FormMessage, FormLabel } from "@/src/components/ui/form";
import { Input } from "@/src/components/ui/input";
import { useFormContext } from "react-hook-form";
import { OfferYachtForm } from "../offer-yacht.schema";
import { YachtPrice } from "./ChangeYachtDetailsModal";
import { calculateCurrencyPrice } from "@/src/lib/utils";
import { useOffersStore } from "@/src/stores/offersStore";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { Amenity, YachtDetails } from "@/src/app/Yachts/model";
import { RadioGroup, RadioGroupItem } from "@/src/components/ui/radio-group";
import { logOfferYachtControlChange, OfferYachtLogContext, useOfferCreatorBlurLogger } from "../../offerCreatorLogs";
const MAX_PERCENTAGE = 100;

// VAT often computes to pennies (e.g. 6.5% of an odd charter fee) — round derived
// amounts to 2dp instead of whole units.
const round2 = (n: number) => Math.round(n * 100) / 100;

// Money amount input that tolerates a decimal point mid-edit: a controlled numeric
// value re-renders "1234." straight back to "1234", eating the keystroke, so hold
// the raw string locally and emit the parsed number (max 2dp).
function DecimalAmountInput({
  value,
  onNumber,
  disabled,
  onFocus,
  onBlur,
}: {
  value: number | undefined;
  onNumber: (n: number) => void;
  disabled?: boolean;
  onFocus?: () => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}) {
  const [raw, setRaw] = useState(value || value === 0 ? String(value) : "");
  const last = useRef(value);
  // Resync when the value changes from outside (percentage edits, price seeding) —
  // but not from our own onNumber, so mid-edit strings aren't clobbered.
  useEffect(() => {
    if (value !== last.current) {
      setRaw(value || value === 0 ? String(value) : "");
      last.current = value;
    }
  }, [value]);
  return (
    <Input
      disabled={disabled}
      inputMode="decimal"
      value={raw}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={(e) => {
        const v = e.target.value;
        if (!/^\d*[.,]?\d{0,2}$/.test(v)) return;
        setRaw(v);
        const n = parseFloat(v.replace(",", "."));
        const parsed = isNaN(n) ? 0 : n;
        last.current = parsed;
        onNumber(parsed);
      }}
    />
  );
}

export type PricingProps = {
  yacht: YachtDetails;
  amenities: Amenity[];
  price: YachtPrice | null;
  offerCurrencyCode: string;
  isLoading: boolean;
  logContext: OfferYachtLogContext;
};
export function Pricing({ price, offerCurrencyCode, yacht, amenities, isLoading, logContext }: PricingProps) {
  const form = useFormContext<OfferYachtForm>();
  const { captureFieldFocus, logOfferYachtControlBlur } = useOfferCreatorBlurLogger();

  const { currencies } = useOffersStore();
  const yachtCurrencyCode = price?.currency || "EUR";
  const operatorCurrencyCode = yacht?.operator?.transferDetails?.currencyCode || "EUR"; // EUR as fallback - agreed with Client
  const baseCurrencyCode = amenities?.find((am) => am.currency)?.currency?.code ?? yachtCurrencyCode;
  const baseCurrency = currencies?.find((c) => c.code === baseCurrencyCode);
  const yachtCurrency = currencies?.find((c) => c.code === yachtCurrencyCode);
  const offerCurrency = currencies?.find((c) => c.code === (offerCurrencyCode ?? "EUR"));
  const operatorCurrency = currencies?.find((c) => c.code === operatorCurrencyCode);

  const calculateBasedPrice = (amount?: number) => {
    if (!amount) return 0;
    return calculateCurrencyPrice(yachtCurrency!, offerCurrency!, amount ?? 0);
  };

  const calculateOfferPrice = (amount: number, currency = offerCurrency?.code) => {
    return new Intl.NumberFormat("en-EN", { style: "currency", currency }).format(amount);
  };

  const calculateCurrency = (amount?: number) => {
    if (!amount) return 0;
    return calculateCurrencyPrice(offerCurrency!, yachtCurrency!, amount ?? 0);
  };

  const yachtAmenities = form.watch("amenitiesDetails");
  const discountCharterPrice = form.watch("discountCharterPrice") ?? 0;
  const customAmenities = form.watch("customAmenities");
  const securityDeposit = form.watch("securityDeposit");
  const pricing = form.watch("pricing");
  const apaPrice = form.watch("pricing.apaPrice");
  const vatPrice = form.watch("pricing.vatPrice");
  const deliveryFeesPrice = form.watch("pricing.deliveryFeesPrice");

  const yachtAmenitiesCustomExcluded = yachtAmenities.filter((am) => !am.isCustomAmenity);
  const extrasPrice =
    yachtAmenitiesCustomExcluded.filter((a) => a.checked && !a.paidLocally).reduce((acc, c) => acc + c.price, 0) +
    customAmenities.filter((a) => a.checked && !a.paidLocally).reduce((acc, c) => acc + c.price, 0);
  const localExtrasPrice =
    yachtAmenities.filter((a) => a.checked && a.paidLocally).reduce((acc, c) => acc + c.price, 0) +
    customAmenities.filter((a) => a.paidLocally).reduce((acc, c) => acc + c.price, 0);
  const extrasPriceInOperatorCurrency = calculateCurrencyPrice(operatorCurrency!, offerCurrency!, extrasPrice);

  const yachtPricing = useMemo(() => {
    const originalPrice = calculateCurrency(price?.listPrice); // list price
    const clientPrice = calculateCurrency(price?.clientPrice); // client price
    const depositPrice = securityDeposit ?? 0; // deposit price
    const discountCharterPriceInOperatorPricing =
      offerCurrencyCode === operatorCurrencyCode
        ? (discountCharterPrice ?? 0)
        : calculateCurrencyPrice(operatorCurrency!, offerCurrency!, discountCharterPrice ?? 0);
    const operatorPayablePrice = price ? price?.operatorPayablePrice : discountCharterPriceInOperatorPricing; // operator payable price, if price isnt fetched make it equal the price client will pay
    const commissionPostHelmDiscountPrice = calculateCurrency(price?.commissionPostHelmDiscount); // commission price
    const clientPricePostHelmDiscount = !!price?.clientPricePostHelmDiscount
      ? price.clientPricePostHelmDiscount
      : discountCharterPrice;

    const operatorTotalPrice =
      (price ? price?.clientPrice : discountCharterPriceInOperatorPricing) + extrasPriceInOperatorCurrency!;
    const checkedAmenities =
      yachtAmenities
        ?.filter((am) => am.checked)
        ?.map((am) => {
          const amenity = amenities.find((a) => a.id === am.id) as Amenity;
          return {
            ...amenity,
            ...am,
          };
        }) ?? [];

    const customExtras =
      customAmenities
        ?.filter((am) => am.checked)
        ?.map((am) => ({
          ...am,
          price: am.finalBookingPrice,
        })) ?? [];

    const checkedAmenitiesCustomExcluded = checkedAmenities.filter((am) => !am.isCustomAmenity);
    const currentExtras = [...checkedAmenitiesCustomExcluded, ...customExtras];

    const includedFree =
      yacht?.amenities?.filter((ex) => ex.included && ex.price === 0)?.map((ex) => ({ text: ex.text })) ?? [];
    const advanceExtras =
      currentExtras
        ?.filter((ex) => !ex.paidLocally && ex.paid && !ex.obligatory && !ex.included)
        ?.map((ex) => ({ text: ex.text, price: ex.price, obligatory: ex.obligatory })) ?? [];
    const advanceObligatory =
      currentExtras
        ?.filter((ex) => !ex.paidLocally && ex.obligatory && ex.paid)
        ?.map((ex) => ({ text: ex.text, price: ex.price })) ?? [];
    const pickupExtras =
      currentExtras
        ?.filter((ex) => ex.paidLocally && ex.paid && !ex.obligatory && !ex.included)
        ?.map((ex) => ({ text: ex.text, price: ex.price })) ?? [];
    const pickupObligatory =
      currentExtras
        ?.filter((ex) => ex.paidLocally && !ex.included && ex.obligatory && ex.paid)
        ?.map((ex) => ({ text: ex.text, price: ex.price })) ?? [];
    // const extrasPrice = [...advanceExtras, ...advanceObligatory]?.reduce((acc, { price }) => acc + price, 0) ?? 0;

    const totalPrice =
      discountCharterPrice! + extrasPrice + (apaPrice ?? 0) + (vatPrice ?? 0) + (deliveryFeesPrice ?? 0); // client price + extras + vat + apa
    const apiDiscountPercentage = price?.helmDiscountPercentage ?? 0;
    const apiDiscount = price?.helmDiscount ?? 0;
    const helmDiscountAmount = calculateCurrency(clientPricePostHelmDiscount)! - discountCharterPrice!;
    const helmDiscountPercentage = Math.round((helmDiscountAmount / calculateCurrency(price?.clientPrice)!) * 100);
    const discountPrice =
      calculateCurrency(price?.clientPricePostHelmDiscount) - discountCharterPrice! + calculateCurrency(apiDiscount); // price discount for helm on operator now, needs to be discussed
    const totalDiscountPercentage =
      Math.round(apiDiscountPercentage + (helmDiscountPercentage * clientPricePostHelmDiscount!) / price?.listPrice!) ||
      0;
    const helmCustomDiscountPrice = calculateCurrency(clientPricePostHelmDiscount) - discountCharterPrice!;
    const clientPriceApi = (!!clientPrice ? clientPrice : discountCharterPrice) || 0;
    return {
      totalPrice: totalPrice || 0, // its the price the client will pay helm - charter + extrasInAdvance
      originalPrice: !!originalPrice ? originalPrice : (discountCharterPrice ?? 0),
      clientPrice: !!clientPrice && clientPrice === discountCharterPrice ? clientPrice : (discountCharterPrice ?? 0), // Client price for charter (excluding extras)
      clientPriceApi,
      depositPrice,
      discountPrice: !!clientPrice ? discountPrice : 0,
      totalDiscountPercentage,
      operatorPayablePrice: +(operatorPayablePrice + extrasPriceInOperatorCurrency).toFixed(2), // total amount of money to be paid to operator by Helm (in operator currency)
      extrasPriceInOperatorCurrency, // extras in advance in operator currency
      operatorTotalPrice, // total clientPrice for the operator pricing(in operator currency) - clientPrice/discountedCharterPrice + extrasInAdvance
      commissionPostHelmDiscountPrice,
      advanceObligatory,
      advanceExtras,
      includedFree,
      pickupExtras,
      pickupObligatory,
      extrasPrice,
      helmCustomDiscountPrice: helmCustomDiscountPrice || 0, // helm discount thats added on top of the rest discounts(api + operator discount)
      localExtrasPrice,
    };
  }, [
    price,
    discountCharterPrice,
    yachtAmenities,
    extrasPrice,
    customAmenities,
    securityDeposit,
    localExtrasPrice,
    apaPrice,
    vatPrice,
    deliveryFeesPrice,
  ]);

  // Tracks the last `price` object we seeded VAT/APA/terms from, so the seed only
  // fires on a real re-fetch — not on the effect's re-runs from VAT/APA keystrokes.
  const lastSeededPrice = useRef<YachtPrice | null>(null);

  useEffect(() => {
    // Seed VAT/APA and charter terms from the fetched price (search-derived) so an
    // exclusive/plus-expenses charter isn't silently defaulted to "inclusive" with
    // its "+ VAT / + APA" lines dropped. Fall back to any prior manual edits.
    //
    // Only pull VAT/APA/terms FROM `price` when a genuinely new price arrives (new
    // dates). This effect also re-runs on every vatPrice/apaPrice change (they're
    // yachtPricing deps), and for providers whose price carries a VAT/APA split
    // (Yachtfolio) re-seeding from `price` on every keystroke snapped the field back
    // to the fetched % before a manual edit could land. Between fetches, keep what
    // the user has entered.
    const priceChanged = lastSeededPrice.current !== price;
    lastSeededPrice.current = price;
    const seededTerms = priceChanged ? (price?.charterTerms ?? pricing.charterTerms) : pricing.charterTerms;
    const seededVatPct = priceChanged ? (price?.vatPercentage ?? pricing.vatPercentage) : pricing.vatPercentage;
    const seededApaPct = priceChanged ? (price?.apaPercentage ?? pricing.apaPercentage) : pricing.apaPercentage;
    const taxBase = yachtPricing.clientPrice ?? 0;
    form.setValue("pricing", {
      depositPrice: yachtPricing.depositPrice,
      discountPrice: yachtPricing.discountPrice,
      discountPercentage: yachtPricing.totalDiscountPercentage,
      originalPrice: yachtPricing.originalPrice,
      clientPrice: yachtPricing.clientPrice,
      clientPriceApi: yachtPricing.clientPriceApi,
      totalPrice: yachtPricing.totalPrice,
      operatorPayablePrice: yachtPricing.operatorPayablePrice,
      operatorTotalPrice: yachtPricing.operatorTotalPrice!,
      extrasPriceInOperatorCurrency: yachtPricing.extrasPriceInOperatorCurrency,
      commissionPostHelmDiscountPrice: yachtPricing.commissionPostHelmDiscountPrice,
      advanceExtras: yachtPricing.advanceExtras,
      advanceObligatory: yachtPricing.advanceObligatory,
      pickupExtras: yachtPricing.pickupExtras,
      pickupObligatory: yachtPricing.pickupObligatory,
      includedFree: yachtPricing.includedFree,
      helmCustomDiscountPrice: yachtPricing.helmCustomDiscountPrice,
      // On a fresh fetch, derive the amount from the seeded %. Between fetches, keep
      // whatever amount is already in the form — so a hand-keyed VAT/APA amount
      // overrides the % (the amount input recomputes the % on change) instead of
      // snapping back to a rounded % * base. The %-field and charter-fee inputs still
      // set the amount directly, so editing those keeps working too.
      vatPrice: priceChanged && seededVatPct ? round2((+seededVatPct / 100) * taxBase) : pricing.vatPrice,
      vatPercentage: seededVatPct,
      apaPercentage: seededApaPct,
      apaPrice: priceChanged && seededApaPct ? round2((+seededApaPct / 100) * taxBase) : pricing.apaPrice,
      deliveryFeesPrice: pricing.deliveryFeesPrice,
      charterTerms: seededTerms,
    });
  }, [yachtPricing]);

  const isCrewed = yacht.provider === "CYA" || yacht.provider === "Helm" || yacht.provider === "Yachtfolio";

  return (
    <>
      <div>
        <h4 className="text-xl font-semibold mb-4">Pricing</h4>
        {!price && (
          <p>
            Choose <b>start date</b> and <b>end date</b> to fetch the pricing
          </p>
        )}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between border-b border-blue-100 pb-2">
            {price && (
              <div className="flex flex-col">
                <span className="text-base">Operator’s client price</span>
                <span className="text-xs text-gray-500">Original price</span>
              </div>
            )}
            {price && (
              <div className="flex flex-col items-end">
                <span className="font-medium text-base">
                  {renderPrice(price?.clientPrice)} {price?.currency}
                </span>
                <span className="flex flex-col items-end text-slate-600 text-sm">
                  {/* Strike the original only when the operator actually discounts it —
                      with no discount this is the SAME price in the offer currency
                      (e.g. "9,098.45 EUR" over a struck "$10,600") and reads as a cut. */}
                  {price?.listPrice > price?.clientPrice && (
                    <span className="line-through">{calculateOfferPrice(yachtPricing.originalPrice)}</span>
                  )}
                  {!!renderPrice(price?.listPrice - price?.clientPrice) && (
                    <span className="text-green-600 text-xs">
                      {renderPrice(price.listPrice - price.clientPrice)} {price.currency} Operator’s discount included
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
          {price && (
            <div className="flex justify-between border-b border-blue-100 pb-2">
              <span className="text-base">Net price payable to the Operator</span>
              <div className="flex flex-col items-end">
                <span className="font-medium text-base">{calculateOfferPrice(yachtPricing.operatorPayablePrice)}</span>
                <span className="text-slate-600 text-sm">
                  {renderPrice(calculateBasedPrice(yachtPricing.operatorPayablePrice))} {price?.currency}
                </span>
              </div>
            </div>
          )}
          {price && (
            <div className="flex justify-between">
              <span className="text-base">Commission fee</span>
              <div className="flex flex-col items-end">
                <span className="font-medium text-base">
                  {calculateOfferPrice(yachtPricing.commissionPostHelmDiscountPrice)}
                </span>
                <span className="text-slate-500 text-sm">
                  {renderPrice(price.commissionPostHelmDiscount)} {price.currency}
                </span>
              </div>
            </div>
          )}
          {price && (
            <div className="flex justify-between">
              <span className="text-base">Discount applied</span>
              <span className="font-medium text-base text-green-600">
                {yachtPricing?.discountPrice > 0 ? (
                  <>
                    {calculateOfferPrice(yachtPricing?.discountPrice)} (-
                    {yachtPricing?.totalDiscountPercentage}%)
                  </>
                ) : (
                  ""
                )}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <div className="flex flex-col">
              <span className="text-base">Discounted charter price</span>
              <span className="text-xs text-gray-500">HELM’s client price</span>
            </div>
            <FormField<OfferYachtForm, "discountCharterPrice">
              name="discountCharterPrice"
              render={({ field: { value, onChange, ...props } }) => (
                <FormItem required>
                  <FormControl>
                    <div className="relative">
                      <Input
                        disabled={isLoading}
                        {...props}
                        value={value}
                        onFocus={() => captureFieldFocus("discountCharterPrice", value)}
                        onChange={(e) => {
                          const val = +e.target.value;
                          form.setValue(
                            "pricing.vatPrice",
                            !!pricing.vatPercentage
                              ? round2(Number((+pricing.vatPercentage / 100) * val))
                              : undefined,
                          );
                          form.setValue(
                            "pricing.apaPrice",
                            !!pricing.apaPercentage
                              ? round2(Number((+pricing.apaPercentage / 100) * val))
                              : undefined,
                          );
                          onChange(val);
                        }}
                        onBlur={(e) => {
                          props.onBlur?.();
                          logOfferYachtControlBlur({
                            eventType: "offer_yacht.pricing_changed",
                            context: logContext,
                            field: "discountCharterPrice",
                            before: value,
                            after: +e.currentTarget.value,
                          });
                        }}
                      />
                      <span className="absolute right-2 top-2">{offerCurrency?.symbol}</span>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex flex-col">
            <span className="text-base">Equipment & Extras</span>
            <div className="flex justify-between">
              <div className="flex flex-col">
                <span className="text-sm">Paid in advance extras</span>
                <span className="text-sm text-gray-500">Paid locally extras</span>
              </div>
              <div className="flex flex-col text-right">
                <span className="font-medium text-base">{calculateOfferPrice(yachtPricing.extrasPrice)}</span>
                {yachtPricing.localExtrasPrice && (
                  <span className="text-base text-gray-500">
                    {calculateCurrencyPrice(baseCurrency!, baseCurrency!, yachtPricing.localExtrasPrice)}{" "}
                    {yachtCurrencyCode}
                  </span>
                )}
              </div>
            </div>
          </div>
          {isCrewed && (
            <CrewedPricing
              form={form}
              offerCurrencySymbol={offerCurrency?.symbol!}
              isLoading={isLoading}
              logContext={logContext}
            />
          )}
          <div className="flex justify-between">
            <span className="text-base text-slate-500">Security deposit</span>
            <div className="flex flex-col items-end">
              <FormField<OfferYachtForm, "securityDeposit">
                name="securityDeposit"
                render={({ field: { value, onChange, ...props } }) => (
                  <FormItem required>
                    <FormControl>
                      <div className="relative">
                        <Input
                          disabled={isLoading}
                          className="!py-1 !px-2 !h-8 !w-20"
                          {...props}
                          value={value}
                          onFocus={() => captureFieldFocus("securityDeposit", value)}
                          onChange={(e) => {
                            const val = +e.target.value;
                            onChange(val);
                          }}
                          onBlur={(e) => {
                            props.onBlur?.();
                            logOfferYachtControlBlur({
                              eventType: "offer_yacht.pricing_changed",
                              context: logContext,
                              field: "securityDeposit",
                              before: value,
                              after: +e.currentTarget.value,
                            });
                          }}
                        />
                        <span className="absolute right-2 top-1">{yachtCurrency?.symbol}</span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <span className="font-medium text-sm text-slate-400">
                {calculateCurrency(yachtPricing?.depositPrice)} {offerCurrencyCode}
              </span>
            </div>
          </div>
          <div className="flex justify-between">
            <span className="text-base">Total amount payable in advance</span>
            <div className="flex flex-col items-end">
              <span className="font-medium text-base">{calculateOfferPrice(yachtPricing.totalPrice)}</span>
              {/* <span className="text-sm text-slate-500">
                {renderPrice(calculateBasedPrice(discountCharterPrice))} EUR
              </span> */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const renderPrice = (price?: number) => {
  if (!price) return null;
  return price.toFixed(2);
};

export const CrewedPricing = ({
  isLoading,
  offerCurrencySymbol,
  form,
  logContext,
}: {
  isLoading?: boolean;
  offerCurrencySymbol: string;
  form: any;
  logContext?: OfferYachtLogContext;
}) => {
  const { captureFieldFocus, logOfferYachtControlBlur } = useOfferCreatorBlurLogger();
  const charterTerms = form.watch("pricing.charterTerms");
  const discountCharterPrice = form.watch("discountCharterPrice") ?? 0;

  const formatPercentageValue = (value: string) => {
    if (!/^\d*([.,]\d{0,1})?$/.test(value)) return null;
    let val = value.replace(",", ".");
    const num = parseFloat(val);
    if (num > MAX_PERCENTAGE) val = MAX_PERCENTAGE.toString();
    return val;
  };

  const handleVatChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = formatPercentageValue(e.target.value);

    if (val !== null) {
      form.setValue("pricing.vatPrice", round2(Number((+val / 100) * discountCharterPrice)));
      form.setValue("pricing.vatPercentage", val);
    } else {
      form.setValue("pricing.vatPercentage", "");
    }
  };

  const handleApaChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = formatPercentageValue(e.target.value);
    if (val !== null) {
      form.setValue("pricing.apaPrice", round2(Number((+val / 100) * discountCharterPrice)));
      form.setValue("pricing.apaPercentage", val);
    } else {
      form.setValue("pricing.apaPercentage", "");
    }
  };

  const disabledInput = isLoading || discountCharterPrice === 0;

  return (
    <>
      <FormField<OfferYachtForm, "pricing.charterTerms">
        name="pricing.charterTerms"
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <FormControl>
              <RadioGroup
                disabled={isLoading}
                className="flex flex-col gap-4 pb-2"
                // Controlled (not defaultValue): the charter terms seed from the fetched
                // price AFTER this mounts, so an uncontrolled radio would stay stuck on the
                // "inclusive" default even when the yacht is exclusive with VAT/APA.
                value={value ? value : "inclusive"}
                onValueChange={(nextValue) => {
                  logContext &&
                    logOfferYachtControlChange({
                      eventType: "offer_yacht.pricing_changed",
                      context: logContext,
                      field: "pricing.charterTerms",
                      before: value,
                      after: nextValue,
                    });
                  const inclusive = nextValue === "inclusive";
                  if (inclusive) {
                    form.setValue("pricing.vatPercentage", "");
                    form.setValue("pricing.vatPrice", 0);
                    form.setValue("pricing.apaPrice", 0);
                    form.setValue("pricing.apaPercentage", "");
                  }
                  onChange(nextValue);
                }}
              >
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <RadioGroupItem value="inclusive" id="inclusive" />
                  </FormControl>
                  <FormLabel className="cursor-pointer !mt-0" htmlFor="inclusive">
                    All inclusive charter terms
                  </FormLabel>
                </FormItem>
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <RadioGroupItem value="exclusive" id="exclusive" />
                  </FormControl>
                  <FormLabel className="cursor-pointer !mt-0" htmlFor="exclusive">
                    VAT & APA exclusive
                  </FormLabel>
                </FormItem>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div
        className={`flex items-center justify-between ${(!charterTerms || charterTerms === "inclusive") && "hidden"}`}
      >
        <span className="text-base">VAT</span>
        <div className="grid gap-2 items-start grid-cols-[1fr,2fr] max-w-[312px]">
          <FormField<OfferYachtForm, "pricing.vatPercentage">
            name="pricing.vatPercentage"
            render={({ field: { value, onChange, ...props } }) => (
              <FormItem required>
                <FormControl>
                  <div className="relative">
                    <Input
                      disabled={disabledInput}
                      {...props}
                      className="flex-1"
                      value={value}
                      onFocus={() => captureFieldFocus("pricing.vatPercentage", value)}
                      onChange={handleVatChange}
                      onBlur={(e) => {
                        props.onBlur?.();
                        logContext &&
                          logOfferYachtControlBlur({
                            eventType: "offer_yacht.pricing_changed",
                            context: logContext,
                            field: "pricing.vatPercentage",
                            before: value,
                            after: form.getValues("pricing.vatPercentage"),
                          });
                      }}
                    />
                    <span className="absolute right-2 top-2">%</span>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField<OfferYachtForm, "pricing.vatPrice">
            name="pricing.vatPrice"
            render={({ field: { value, onChange, ...props } }) => (
              <FormItem required>
                <FormControl>
                  <div className="relative">
                    <DecimalAmountInput
                      disabled={disabledInput}
                      value={value}
                      onFocus={() => captureFieldFocus("pricing.vatPrice", value)}
                      onNumber={(n) => {
                        let val = n;
                        if (val > discountCharterPrice) {
                          val = discountCharterPrice;
                        }
                        const percentage = Number((val / discountCharterPrice) * 100).toFixed(1);
                        form.setValue("pricing.vatPercentage", percentage);
                        onChange(val);
                      }}
                      onBlur={(e) => {
                        props.onBlur?.();
                        logContext &&
                          logOfferYachtControlBlur({
                            eventType: "offer_yacht.pricing_changed",
                            context: logContext,
                            field: "pricing.vatPrice",
                            before: value,
                            after: form.getValues("pricing.vatPrice"),
                          });
                      }}
                    />
                    <span className="absolute right-2 top-2">{offerCurrencySymbol}</span>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      <div
        className={`flex items-center justify-between ${(!charterTerms || charterTerms === "inclusive") && "hidden"}`}
      >
        <span className="text-base">APA</span>
        <div className="grid gap-2 items-start grid-cols-[1fr,2fr] max-w-[312px]">
          <FormField<OfferYachtForm, "pricing.apaPercentage">
            name="pricing.apaPercentage"
            render={({ field: { value, onChange, ...props } }) => (
              <FormItem required>
                <FormControl>
                  <div className="relative">
                    <Input
                      disabled={disabledInput}
                      {...props}
                      className="flex-1"
                      value={value}
                      onFocus={() => captureFieldFocus("pricing.apaPercentage", value)}
                      onChange={handleApaChange}
                      onBlur={(e) => {
                        props.onBlur?.();
                        logContext &&
                          logOfferYachtControlBlur({
                            eventType: "offer_yacht.pricing_changed",
                            context: logContext,
                            field: "pricing.apaPercentage",
                            before: value,
                            after: form.getValues("pricing.apaPercentage"),
                          });
                      }}
                    />
                    <span className="absolute right-2 top-2">%</span>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField<OfferYachtForm, "pricing.apaPrice">
            name="pricing.apaPrice"
            render={({ field: { value, onChange, ...props } }) => (
              <FormItem required>
                <FormControl>
                  <div className="relative">
                    <DecimalAmountInput
                      disabled={disabledInput}
                      value={value}
                      onFocus={() => captureFieldFocus("pricing.apaPrice", value)}
                      onNumber={(n) => {
                        let val = n;
                        if (val > discountCharterPrice) {
                          val = discountCharterPrice;
                        }
                        const percentage = Number((val / discountCharterPrice) * 100).toFixed(1);
                        form.setValue("pricing.apaPercentage", percentage);
                        onChange(val);
                      }}
                      onBlur={(e) => {
                        props.onBlur?.();
                        logContext &&
                          logOfferYachtControlBlur({
                            eventType: "offer_yacht.pricing_changed",
                            context: logContext,
                            field: "pricing.apaPrice",
                            before: value,
                            after: form.getValues("pricing.apaPrice"),
                          });
                      }}
                    />
                    <span className="absolute right-2 top-2">{offerCurrencySymbol}</span>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      <div className="flex justify-between">
        <span className="text-base">Delivery fees</span>
        <div className="flex flex-col items-end">
          <FormField<OfferYachtForm, "pricing.deliveryFeesPrice">
            name="pricing.deliveryFeesPrice"
            render={({ field: { value, onChange, ...props } }) => (
              <FormItem required>
                <FormControl>
                  <div className="relative">
                    <Input
                      disabled={isLoading}
                      {...props}
                      value={value}
                      onFocus={() => captureFieldFocus("pricing.deliveryFeesPrice", value)}
                      onChange={(e) => {
                        const val = +e.target.value;
                        onChange(val);
                      }}
                      onBlur={(e) => {
                        props.onBlur?.();
                        logContext &&
                          logOfferYachtControlBlur({
                            eventType: "offer_yacht.pricing_changed",
                            context: logContext,
                            field: "pricing.deliveryFeesPrice",
                            before: value,
                            after: +e.currentTarget.value,
                          });
                      }}
                    />
                    <span className="absolute right-2 top-2">{offerCurrencySymbol}</span>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </>
  );
};
